import { useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Upload, Loader2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, errMsg } from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { DocumentViewer } from "@/components/DocumentViewer";

export const DOC_LABELS = {
  id_card: "Documento d'identità",
  health_card: "Tessera sanitaria",
  chamber: "Visura camerale aggiornata",
  id_card_front: "Documento d'identità – fronte",
  id_card_back: "Documento d'identità – retro",
  health_card_front: "Tessera sanitaria – fronte",
  health_card_back: "Tessera sanitaria – retro",
};
export const REQUIRED_DOCS = {
  privato: ["id_card_front", "id_card_back", "health_card_front", "health_card_back"],
  rivenditore: ["id_card_front", "id_card_back", "health_card_front", "health_card_back", "chamber"],
};

const DocRow = ({ kind }) => {
  const api = useApi();
  const { me, setMe } = useUser();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(false);
  const doc = me?.documents?.[kind];

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api("post", `/me/documents/${kind}`, fd);
      setMe((p) => ({ ...p, documents: { ...(p.documents || {}), [kind]: r } }));
      toast.success(`${DOC_LABELS[kind]} caricato`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid={`doc-row-${kind}`} className={`flex items-center gap-3 p-3 rounded-xl border ${doc ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white"}`}>
      {doc ? <CheckCircle2 size={18} className="text-emerald-600 shrink-0" /> : <Upload size={18} className="text-slate-400 shrink-0" />}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{DOC_LABELS[kind]}</p>
        <p className="text-xs text-slate-500 truncate" data-testid={`doc-status-${kind}`}>{doc ? doc.name : "Immagine o PDF, max 10MB"}</p>
      </div>
      {doc && doc.path !== "none" && (
        <Button type="button" size="sm" variant="ghost" onClick={() => setView(true)} data-testid={`doc-view-${kind}`}><Eye size={14} /></Button>
      )}
      <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => input.current?.click()} data-testid={`doc-upload-button-${kind}`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : doc ? "Sostituisci" : "Carica"}
      </Button>
      <input ref={input} data-testid={`doc-upload-input-${kind}`} type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} />
      {view && <DocumentViewer userId={me.user_id} kind={kind} title={DOC_LABELS[kind]} onClose={() => setView(false)} />}
    </div>
  );
};

export const DocumentUploads = ({ accountType }) => (
  <div className="space-y-2" data-testid="document-uploads">
    <p className="text-sm font-medium">Documenti richiesti</p>
    {REQUIRED_DOCS[accountType].map((k) => <DocRow key={k} kind={k} />)}
  </div>
);
